import type { Placement } from "react-aria";
import { Collection, Menu } from "react-aria-components";
import type {
  MultiSelectOption,
  MultiSelectSection,
} from "../Select/multiSelect.js";
import { SimpleMenuItem } from "./simpleMenuItem.js";
import { longMenuMaxHeight, SimpleMenuPopover } from "./simpleMenuPopover.js";
import { SimpleMenuSection } from "./simpleMenuSection.js";

export interface MultiSelectMenuPopoverProps {
  /** Accessible label for the menu. */
  ariaLabel: string;
  placement?: Placement;
  sections: readonly MultiSelectSection[];
  value: readonly string[];
  onChange: (value: readonly string[]) => void;
}

/**
 * The popover + multi-select `Menu` for use inside a `MenuTrigger`. The
 * caller is responsible for providing the trigger as a sibling.
 */
export const MultiSelectMenuPopover = (props: MultiSelectMenuPopoverProps) => {
  return (
    <SimpleMenuPopover
      maxHeight={longMenuMaxHeight}
      placement={props.placement}
    >
      <Menu
        aria-label={props.ariaLabel}
        items={props.sections}
        selectionMode="multiple"
        selectedKeys={props.value}
        onSelectionChange={(keys) => {
          if (keys === "all") {
            return;
          }
          props.onChange(
            Array.from(keys).filter((k): k is string => typeof k === "string"),
          );
        }}
      >
        {(section) => (
          <SimpleMenuSection id={section.id} label={section.label}>
            <Collection items={section.options}>
              {(option: MultiSelectOption) => (
                <SimpleMenuItem
                  id={option.id}
                  indent={option.indent}
                  textValue={option.label}
                >
                  {option.icon ? (
                    <>
                      <div className="shrink-0">{option.icon}</div>
                      <span className="flex-1 truncate">{option.label}</span>
                    </>
                  ) : (
                    option.label
                  )}
                </SimpleMenuItem>
              )}
            </Collection>
          </SimpleMenuSection>
        )}
      </Menu>
    </SimpleMenuPopover>
  );
};
